import classNames from "classnames";
import { useState } from "react";
import Avatar from "@/components/Avatar";
import EmptyState from "@/components/EmptyState";
import { TableCard, RowLink, TableCardRow } from "@/components/Table";
import InteractionSideSheet from "@/features/global/components/InteractionSideSheet";
import useRecentInteractions from "@/pages/home/_hooks/useRecentInteractions";
import { useIsDashboardStackedTableRows } from "@/pages/home/_hooks/useIsDashboardSingleColumn";
import {
  formatInteractionRecencyLabel,
  getInteractionDescription,
  getInteractionInitials,
  getInteractionParticipantLabel,
  getInteractionRecencyTimestamp,
  getPrimaryParticipantPgaId
} from "@/pages/home/_utils/interactionDisplay";
import type { RecentInteractionNode } from "@/pages/home/queries";
import { type Path, useNavigate } from "@/router";
import DashboardTableSkeleton from "./DashboardTableSkeleton";
import {
  DASHBOARD_PANEL_SHELL_CLASS,
  DASHBOARD_TABLE_CONTENT_CELL,
  DASHBOARD_TABLE_META_CELL
} from "../_utils/dashboardPanelStyles";
import { DASHBOARD_TABLE_CARD_BODY_CLASS, getDashboardPreviewTableClassName } from "../_utils/dashboardPreviewTable";

const COLUMN_COUNT = 2;

function InitialsAvatar({ name, picture }: { name: string; picture?: string }) {
  if (picture) {
    return <Avatar picture={picture} />;
  }

  return (
    <div
      className="flex h-12 w-12 min-w-[3rem] items-center justify-center rounded-full bg-sys-brand-secondary-container"
      data-testid="recent-interaction-initials-avatar"
    >
      <span className="text-label-large text-sys-brand-on-surface-variant">{getInteractionInitials(name)}</span>
    </div>
  );
}

function InteractionDetails({ interaction }: { interaction: RecentInteractionNode }) {
  const participantLabel = getInteractionParticipantLabel(interaction.participants, interaction.relatedTo);
  const description = getInteractionDescription(interaction);
  const primaryParticipant = interaction.participants[0];

  return (
    <div className="flex min-w-0 items-center">
      <InitialsAvatar name={participantLabel} picture={primaryParticipant?.profilePhoto} />
      <div className="ml-2 min-w-0">
        <div className="line-clamp-2 text-body-medium">{participantLabel}</div>
        {description ? (
          <div className="text-body-small text-sys-brand-on-surface-variant line-clamp-2">{description}</div>
        ) : null}
      </div>
    </div>
  );
}

function InteractionAtLabel({ interaction }: { interaction: RecentInteractionNode }) {
  const label = formatInteractionRecencyLabel(getInteractionRecencyTimestamp(interaction));

  return <span className="text-body-small whitespace-nowrap text-sys-brand-on-surface-variant">{label ?? ""}</span>;
}

type RecentInteractionRowProps = {
  interaction: RecentInteractionNode;
  onOpen: (interaction: RecentInteractionNode) => void;
};

function RecentInteractionRow({ interaction, onOpen }: RecentInteractionRowProps) {
  const participantLabel = getInteractionParticipantLabel(interaction.participants, interaction.relatedTo);
  const isStackedTableRows = useIsDashboardStackedTableRows();

  return (
    <TableCardRow
      columnCount={COLUMN_COUNT}
      rowTestId={`recent-interaction-row-${interaction.id}`}
      onClick={() => onOpen(interaction)}
      ariaLabel={`Open interaction with ${participantLabel}`}
      stackedLayout={isStackedTableRows}
      mobile={
        <div className="flex min-w-0 flex-col gap-2">
          <InteractionDetails interaction={interaction} />
          <InteractionAtLabel interaction={interaction} />
        </div>
      }
    >
      <td className={classNames(DASHBOARD_TABLE_CONTENT_CELL, "max-w-0")}>
        <InteractionDetails interaction={interaction} />
      </td>
      <td className={DASHBOARD_TABLE_META_CELL}>
        <InteractionAtLabel interaction={interaction} />
      </td>
    </TableCardRow>
  );
}

export default function RecentInteractionsTable() {
  const navigate = useNavigate();
  const isStackedTableRows = useIsDashboardStackedTableRows();
  const { interactions, loading, hasMore, loadMore } = useRecentInteractions();
  const [selectedInteraction, setSelectedInteraction] = useState<RecentInteractionNode | null>(null);

  const isEmpty = !loading && interactions.length === 0;
  const primaryParticipantPgaId = selectedInteraction
    ? getPrimaryParticipantPgaId(selectedInteraction.participants)
    : null;

  return (
    <>
      <TableCard
        title="Recent Interactions"
        testId="home-recent-interactions-card"
        bodyClassName={DASHBOARD_TABLE_CARD_BODY_CLASS}
        shellClassName={DASHBOARD_PANEL_SHELL_CLASS}
      >
        <table
          className={getDashboardPreviewTableClassName(isStackedTableRows)}
          data-testid="home-recent-interactions-table"
        >
          {isStackedTableRows ? null : (
            <colgroup>
              <col />
              <col className="w-28" />
            </colgroup>
          )}
          <tbody>
            {loading ? (
              <DashboardTableSkeleton columnCount={COLUMN_COUNT} />
            ) : isEmpty && !hasMore ? (
              <tr>
                <td colSpan={COLUMN_COUNT}>
                  <EmptyState iconName="record_voice_over" title="No Recent Interactions" />
                </td>
              </tr>
            ) : (
              <>
                {interactions.map((interaction) => (
                  <RecentInteractionRow
                    key={interaction.id}
                    interaction={interaction}
                    onOpen={setSelectedInteraction}
                  />
                ))}
                {hasMore ? (
                  <RowLink columnCount={COLUMN_COUNT} onClick={loadMore} testId="recent-interactions-load-more">
                    Load More
                  </RowLink>
                ) : null}
              </>
            )}
          </tbody>
        </table>
      </TableCard>

      <InteractionSideSheet
        open={selectedInteraction !== null}
        onClose={() => setSelectedInteraction(null)}
        interactionId={selectedInteraction?.id ?? null}
        pgaId={primaryParticipantPgaId}
        customActionButtons={
          selectedInteraction && primaryParticipantPgaId
            ? [
                {
                  text: "View Interaction",
                  variant: "outlined",
                  testId: "view-recent-interaction",
                  onClick: () => {
                    navigate(
                      `/people/${primaryParticipantPgaId}/interactions/interactions/${selectedInteraction.id}` as Path
                    );
                  }
                }
              ]
            : undefined
        }
      />
    </>
  );
}
